import { jsPDF } from "jspdf";
import escudoBase64 from "../../assets/img/escudo_universidad.webp";
import firma from "../../assets/img/firma_marcelo_bellini.webp";

/** Carga tamaño imagen base64 */
const loadImageSize = (base64) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => resolve({ width: img.width, height: img.height });
  });

function drawTwoColumnTable(
  pdf,
  startX,
  startY,
  totalWidth,
  rowHeight,
  rows,
  options = {}
) {
  const colWidth = totalWidth / 2;
  const rowHeights = Array.isArray(rowHeight)
    ? rowHeight
    : rows.map(() => rowHeight);
  const {
    font = "Arial",
    fontSize = 10,
    textColor = [0, 0, 0],
    borderColor = [0, 0, 0],
    borderWidth = 0.5,
    padding = 4,
    alignLeft = "left",
    alignRight = "left",
    boldLeft = false,
    boldRight = false,
  } = options;

  pdf.setFont(font);
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...textColor);
  pdf.setDrawColor(...borderColor);
  pdf.setLineWidth(borderWidth);

  let currentY = startY;

  rows.forEach((row, i) => {
    const currentRowHeight = rowHeights[i] || rowHeight;
    const y = currentY;
    const leftLines = Array.isArray(row[0])
      ? row[0]
      : String(row[0] ?? "").split("\n");
    const rightLines = Array.isArray(row[1])
      ? row[1]
      : String(row[1] ?? "").split("\n");
    const lineStep = fontSize + 2;

    pdf.rect(startX, y, colWidth, currentRowHeight);
    pdf.rect(startX + colWidth, y, colWidth, currentRowHeight);

    const leftTextHeight = Math.max(lineStep, leftLines.length * lineStep);
    const rightTextHeight = Math.max(lineStep, rightLines.length * lineStep);
    const leftStartY = y + Math.max(padding + fontSize - 1, (currentRowHeight - leftTextHeight) / 2 + fontSize);
    const rightStartY = y + Math.max(padding + fontSize - 1, (currentRowHeight - rightTextHeight) / 2 + fontSize);

    pdf.setFont(font, boldLeft ? "bold" : "normal");
    leftLines.forEach((line, lineIndex) => {
      pdf.text(line, startX + padding, leftStartY + lineIndex * lineStep, {
        align: alignLeft,
        maxWidth: colWidth - 2 * padding,
      });
    });

    pdf.setFont(font, boldRight ? "bold" : "normal");
    rightLines.forEach((line, lineIndex) => {
      pdf.text(line, startX + colWidth + padding, rightStartY + lineIndex * lineStep, {
        align: alignRight,
        maxWidth: colWidth - 2 * padding,
      });
    });

    currentY += currentRowHeight;
  });
}

async function drawHeader(pdf, data, marginX = 40, cursorY = 40) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const cuadroTotalW = pageWidth - marginX * 2;
  const cuadroTotalH = 180;
  const cuadroTotalX = marginX;
  const cuadroTotalY = cursorY;

  pdf.setLineWidth(1);
  pdf.setDrawColor(0);
  pdf.rect(cuadroTotalX, cuadroTotalY, cuadroTotalW, cuadroTotalH);

  pdf.line(
    cuadroTotalX + cuadroTotalW / 2,
    cuadroTotalY,
    cuadroTotalX + cuadroTotalW / 2,
    cuadroTotalY + cuadroTotalH
  );

  const boxW = 85;
  const boxH = 80;
  const boxX = (pageWidth - boxW) / 2;
  const boxY = cursorY;

  pdf.setFillColor(255, 255, 255);
  pdf.rect(boxX, boxY, boxW, boxH, "FD");

  pdf.setFont("Arial", "bold").setFontSize(18);
  pdf.text("X", boxX + boxW / 2, boxY + 24, { align: "center" });

  pdf.setFont("Arial", "normal").setFontSize(12);
  pdf.text("Documento no", boxX + boxW / 2, boxY + 44, { align: "center" });
  pdf.text("válido como", boxX + boxW / 2, boxY + 58, { align: "center" });
  pdf.text("factura", boxX + boxW / 2, boxY + 72, { align: "center" });

  const colW = cuadroTotalW / 2;
  const colLeftX = cuadroTotalX;
  const colLeftY = cuadroTotalY + 10;

  const { width: imgW, height: imgH } = await loadImageSize(escudoBase64);
  const escudoWidth = 80;
  const escudoHeight = (imgH / imgW) * escudoWidth;

  pdf.addImage(
    escudoBase64,
    "WEBP",
    colLeftX + (colW - escudoWidth) / 2,
    colLeftY,
    escudoWidth,
    escudoHeight
  );

  pdf.setFont("Cambria", "normal").setFontSize(10);
  const textosIzquierda = [
    "Universidad Nacional de San Juan",
    "Secretaria de Ciencia y Tecnica",
    "Unidad de Vinculacion y Transferencia",
    "I.V.A Exento",
  ];

  textosIzquierda.forEach((linea, i) => {
    if (linea === "Universidad Nacional de San Juan") {
      pdf.setFont("Calibri", "bold");
    } else {
      pdf.setFont("Cambria", "normal").setFontSize(10);
    }
    pdf.text(
      linea,
      colLeftX + colW / 2,
      colLeftY + escudoHeight + 14 + i * 12,
      {
        align: "center",
      }
    );
  });

  const colRightX = cuadroTotalX + colW;
  let colRightY = cuadroTotalY + 10;

  pdf.setFont("Calibri", "normal").setFontSize(10);
  pdf.text(
    "Orden de Servicios (art. 12 Ord. 25/16 CS)",
    colRightX + 50,
    colRightY
  );
  colRightY += 14;
  pdf.text("Unidad Tecnica:", colRightX + 50, colRightY);
  colRightY += 26;

  pdf.setFont("Calibri", "bold");
  pdf.text(
    "Instituto de Investigaciones Mineras",
    colRightX + colW / 2,
    colRightY,
    { align: "center" }
  );
  colRightY += 14;

  pdf.setFont("Calibri", "normal");
  pdf.setTextColor(0, 0, 255);

  const mail = "transferenciaiim@unsj.edu.ar";
  const x = colRightX + colW / 2;
  const y = colRightY;

  pdf.setFont("Calibri", "bold");
  pdf.text(mail, x, y, { align: "center" });
  pdf.link(x - pdf.getTextWidth(mail) / 2, y - 10, pdf.getTextWidth(mail), 12, {
    url: `mailto:${mail}`,
  });

  const textWidth = pdf.getTextWidth(mail);
  const underlineY = y + 2;
  pdf.setDrawColor(0, 0, 255);
  pdf.setLineWidth(0.5);
  pdf.line(x - textWidth / 2, underlineY, x + textWidth / 2, underlineY);

  pdf.setTextColor(0, 0, 0);
  colRightY += 14;

  pdf.text("Tel: +54 264 4220556 interno 23", colRightX + colW / 2, colRightY, {
    align: "center",
  });
  colRightY += 18;

  pdf.setFont("Calibri", "normal");
  const laboratorioTexto = `Laboratorio: ${data.laboratorio || ""}`;
  const laboratorioLineas = pdf.splitTextToSize(laboratorioTexto, colW - 60);
  pdf.text(laboratorioLineas, colRightX + 50, colRightY);
  colRightY += laboratorioLineas.length * 12;
  pdf.text(`Nº: ${data.numero}`, colRightX + 50, colRightY);
  colRightY += 12;
  // pdf.text("Fecha: 00/00/2000", colRightX + 50, colRightY);
  pdf.text(`Fecha: ${data.fecha.replace(/-/g, "/")}`, colRightX + 50, colRightY);
  colRightY += 16;

  pdf.text("C.U.I.T Nº: 30-58676233-4", colRightX + 10, colRightY);
  colRightY += 14;
  pdf.text("Ing. Brutos: no contribuyente", colRightX + 10, colRightY);
  colRightY += 14;
  pdf.text("Inicio de Actividad", colRightX + 10, colRightY);

  return cuadroTotalY + cuadroTotalH + 30;
}

function drawClientInfoTable(pdf, data, startX = 40, startY = 250) {
  const cellWidth = 260;
  const cellHeight = 20;
  const lineHeight = 12;
  const textPadding = 8;
  const safeEmails = String(data.emailContacto || "")
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean)
    .join(", ");

  pdf.setFont("Arial", "normal");
  pdf.setFontSize(10);

  const rows = [
    [
      `Nombre del Solicitante: ${data.solicitante || ""}`,
      `Cod. Postal: ${data.codigoPostal || ""}`,
    ],
    [
      `Domicilio: ${data.domicilio || ""}`,
      `Localidad: ${data.localidad || ""}`,
    ],
    [`Cond. de venta: ${data.condVenta || ""}`, "Provincia: San Juan"],
    [`I.V.A: ${data.iva || ""}`, `Teléfono: ${data.telefono || ""}`],
    [
      `C.U.I.T N°: ${data.cuit || ""}`,
      "Tipo de Cambio BNA Vendedor: DIVISA",
    ],
    ["", `Persona de Contacto: ${data.personaContacto || ""}`],
    [
      `Presupuesto: ${data.codigoPresupuesto || ""}`,
      `e-mail: ${safeEmails}`,
    ],
  ];

  const processedRows = rows.map(([leftText, rightText]) => {
    const leftLines = pdf.splitTextToSize(leftText || "", cellWidth - textPadding);
    const rightLines = pdf.splitTextToSize(rightText || "", cellWidth - textPadding);
    return [leftLines, rightLines];
  });

  const rowHeights = processedRows.map(([leftLines, rightLines]) => {
    const leftHeight = Math.max(1, leftLines.length) * lineHeight + textPadding;
    const rightHeight = Math.max(1, rightLines.length) * lineHeight + textPadding;
    return Math.max(cellHeight, leftHeight, rightHeight);
  });

  drawTwoColumnTable(pdf, startX, startY, cellWidth * 2, rowHeights, processedRows, {
    font: "Arial",
    fontSize: 10,
    alignLeft: "left",
    alignRight: "left",
  });

  const totalHeight = rowHeights.reduce((sum, value) => sum + value, 0);
  return startY + totalHeight + 30;
}

function drawImporte(
  pdf,
  importeX,
  importeY,
  importe,
  importeEnTexto,
  moneda = "USD",
  tipoCambio = 1,
  pageWidth = 595
) {
  pdf.setFontSize(12);
  const fontNormal = "arial";
  const fontBold = "arial";

  // Convertir a pesos si viene en dólares
  const importeEnPesos =
    (Number(importe) || 0) * (moneda === "USD" ? tipoCambio : 1);

  // Formatear con separador de miles
  const importeFormateado = importeEnPesos.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Separar "<texto> con XX/100" → "<texto>" y "XX/100" (solo si tiene centavos)
  const partesTexto = importeEnTexto.split(" con ");
  const textoPalabras = partesTexto[0] || importeEnTexto;
  const centavosParte = partesTexto[1] || null;
  const currencyLabel = moneda === "USD" ? "dólares estadounidenses" : "pesos argentinos";

  const text1 = "Importe: ";
  const simbolo = moneda === "USD" ? "U$" : "AR$";
  const text2 = `${simbolo}${importeFormateado}`;
  const text3 = centavosParte
    ? ` (Son ${textoPalabras} ${currencyLabel} con ${centavosParte})`
    : ` (Son ${textoPalabras} ${currencyLabel})`;

  const maxWidth = pageWidth - importeX - 40;

  pdf.setFont(fontNormal, "normal");
  pdf.text(text1, importeX, importeY);
  const text1Width = pdf.getTextWidth(text1);

  pdf.setFont(fontBold, "bold");
  pdf.text(text2, importeX + text1Width, importeY);
  const text2Width = pdf.getTextWidth(text2);

  // text3 puede ser largo: dividirlo si hace falta
  pdf.setFont(fontNormal, "normal");
  const lineas = pdf.splitTextToSize(text3, maxWidth - text1Width - text2Width);
  if (lineas.length === 1) {
    pdf.text(lineas[0], importeX + text1Width + text2Width, importeY);
  } else {
    // Primera línea en la misma fila, el resto con sangría
    pdf.text(lineas[0], importeX + text1Width + text2Width, importeY);
    for (let i = 1; i < lineas.length; i++) {
      pdf.text(lineas[i], importeX + text1Width, importeY + i * 14);
    }
    return importeY + lineas.length * 14 + 11;
  }

  return importeY + 25;
}

function drawFormaPago(pdf, pageWidth, cursorY) {
  pdf.setFontSize(9);
  pdf.text(
    "Forma de pago: La forma de pago es por transferencia bancaria, depósito en cuenta del Banco Nación Arentina, Universidad Nacional de San Juan, CUIT. N° 30-58676233-4, CBU N°: 0110474920047420090763",
    40,
    cursorY,
    { maxWidth: pageWidth - 80 }
  );

  // Agregar todo el texto nuevo en negrita
  const newCursorY = cursorY + 30;
  pdf.setFont("Arial", "bold");
  pdf.text(
    "Deberá abonarse el 50% al momento de recibir la Factura emitida por UVT, y el 50% restante contra entrega de informe final. Caso contrario La Empresa deberá emitir Orden de Compra por el 100% del presupuesto",
    40,
    newCursorY,
    { maxWidth: pageWidth - 80 }
  );

  return newCursorY + 35;
}

function drawNotaEmails(pdf, cursorY) {
  const notaTexto =
    "NOTA: Una vez realizado el pago, adjuntar comprobante de la transferencia a ";
  const email1 = "lgarcia@unsj.edu.ar";
  const email2 = "csalem@unsj.edu.ar";

  const startX = 40;
  let xNota = startX;
  const yNota = cursorY;

  pdf.setFont("Arial", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(notaTexto, xNota, yNota);

  xNota += pdf.getTextWidth(notaTexto);

  pdf.setTextColor(0, 0, 255);
  pdf.text(email1, xNota + 2, yNota);
  pdf.link(xNota, yNota - 10, pdf.getTextWidth(email1), 12, {
    url: `mailto:${email1}`,
  });
  xNota += pdf.getTextWidth(email1);

  const comaEspacio = ", ";
  pdf.setTextColor(0, 0, 0);
  pdf.text(comaEspacio, xNota + 1, yNota);
  xNota += pdf.getTextWidth(comaEspacio);

  pdf.setTextColor(0, 0, 255);
  pdf.text(email2, xNota, yNota);
  pdf.link(xNota, yNota - 10, pdf.getTextWidth(email2), 12, {
    url: `mailto:${email2}`,
  });
  xNota += pdf.getTextWidth(email2);

  pdf.setTextColor(0, 0, 0);

  return yNota + 20;
}

function drawCondicionesGenerales(pdf, pageWidth, cursorY) {
  pdf.setFont("Times New Roman", "normal");
  pdf.setTextColor("#002060");
  pdf.text(
    "Este informe será elaborado y facturado exclusivamente en base a los datos suministrados por el solicitante/empresa, que figuran en la Orden de Servicio de la UNSJ. El documento es intransferible y específico para el requerimiento de este servicio.",
    40,
    cursorY,
    { maxWidth: pageWidth - 80 }
  );
  return cursorY + 30;
}

function drawPlazoYValidez(pdf, pageWidth, cursorY, data) {
  const startX = 40;
  pdf.setFont("Arial", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor("#000000");

  const plazoRaw = String(data.plazoEntrega || "").trim();
  let plazoFormateado = "Sin plazo";

  if (plazoRaw) {
    const soloFecha = plazoRaw.includes("T") ? plazoRaw.split("T")[0] : plazoRaw;

    if (/^\d{4}-\d{2}-\d{2}$/.test(soloFecha)) {
      const [anio, mes, dia] = soloFecha.split("-");
      plazoFormateado = `${dia}/${mes}/${anio}`;
    } else {
      const fechaObj = new Date(plazoRaw);
      if (!Number.isNaN(fechaObj.getTime())) {
        const dia = String(fechaObj.getDate()).padStart(2, "0");
        const mes = String(fechaObj.getMonth() + 1).padStart(2, "0");
        const anio = String(fechaObj.getFullYear());
        plazoFormateado = `${dia}/${mes}/${anio}`;
      }
    }
  }

  pdf.setFont("Arial", "bold");
  pdf.text("Plazo de entrega del informe: ", startX, cursorY);
  const labelWidth = pdf.getTextWidth("Plazo de entrega del informe: ");
  pdf.setFont("Arial", "normal");
  pdf.text(plazoFormateado, startX + labelWidth, cursorY);
  cursorY += 18;

  return cursorY;
}

function drawDetalleTrabajo(pdf, data, detalleX, detalleY, pageWidth) {
  const marginTop = 40;
  const marginBottom = 40;
  const pageHeight = pdf.internal.pageSize.height;
  const maxY = pageHeight - marginBottom;

  const tableWidth = pageWidth - 80;
  const col1W = tableWidth * 0.5;
  const col2W = tableWidth * 0.1;
  const col3W = tableWidth * 0.2;
  const col4W = tableWidth * 0.2;

  const paddingX = 5;
  const lineHeight = 12;

  pdf.setFontSize(10);
  pdf.setFont("Arial", "bold");

  // Encabezado de tabla
  pdf.rect(detalleX, detalleY, tableWidth, 20);
  pdf.line(detalleX + col1W, detalleY, detalleX + col1W, detalleY + 20);
  pdf.line(
    detalleX + col1W + col2W,
    detalleY,
    detalleX + col1W + col2W,
    detalleY + 20
  );
  pdf.line(
    detalleX + col1W + col2W + col3W,
    detalleY,
    detalleX + col1W + col2W + col3W,
    detalleY + 20
  );

  const headerTextY = detalleY + 14;
  pdf.text("Detalle del trabajo a realizar", detalleX + paddingX, headerTextY);
  pdf.text("Cantidad", detalleX + col1W + paddingX, headerTextY);
  pdf.text(
    `Precio Unitario (${data.moneda || "ARS"})`,
    detalleX + col1W + col2W + paddingX,
    headerTextY
  );
  pdf.text(
    `Precio Total (${data.moneda || "ARS"})`,
    detalleX + col1W + col2W + col3W + paddingX,
    headerTextY
  );

  detalleY += 20;
  pdf.setFont("Arial", "normal");

  const nuevaPagina = () => {
    pdf.addPage();
    detalleY = marginTop;
  };

  const tipoCambio = Number(data.tipoCambio || 1);
  const esARS = data.moneda === "ARS";

  data.items.forEach((item) => {
    const descripcionLines = pdf.splitTextToSize(
      item.descripcion || `${item.itemName ?? "Sin nombre"}` || "Sin descripción",
      col1W - 2 * paddingX
    );
    const rowHeight = Math.max(20, descripcionLines.length * lineHeight);

    if (detalleY + rowHeight > maxY) {
      nuevaPagina();
    }

    pdf.rect(detalleX, detalleY, col1W, rowHeight);
    pdf.rect(detalleX + col1W, detalleY, col2W, rowHeight);
    pdf.rect(detalleX + col1W + col2W, detalleY, col3W, rowHeight);
    pdf.rect(detalleX + col1W + col2W + col3W, detalleY, col4W, rowHeight);

    let textY = detalleY + lineHeight;
    descripcionLines.forEach((line) => {
      pdf.text(line, detalleX + paddingX, textY);
      textY += lineHeight;
    });

    // Conversión segura a número
    const cantidad = Number(item.cantidad || 0);
    const unitario = Number(item.precioUnitario || 0);

    // Convertimos a ARS si originalmente estaba en USD
    const unitarioEnPesos = esARS ? unitario : unitario * tipoCambio;
    const totalEnPesos = cantidad * unitarioEnPesos;

    const centerY = detalleY + rowHeight / 2 + 4;

    pdf.text(
      cantidad.toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      detalleX + col1W + paddingX,
      centerY
    );

    pdf.text(
      unitarioEnPesos.toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      detalleX + col1W + col2W + paddingX,
      centerY
    );

    pdf.text(
      totalEnPesos.toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      detalleX + col1W + col2W + col3W + paddingX,
      centerY
    );

    detalleY += rowHeight;
  });

  // Fila total
  const totalRowHeight = 20;
  if (detalleY + totalRowHeight > maxY) {
    nuevaPagina();
  }

  pdf.setFont("Arial", "bold");
  pdf.rect(detalleX, detalleY, col1W, totalRowHeight);
  pdf.rect(detalleX + col1W, detalleY, col2W, totalRowHeight);
  pdf.rect(detalleX + col1W + col2W, detalleY, col3W, totalRowHeight);
  pdf.rect(detalleX + col1W + col2W + col3W, detalleY, col4W, totalRowHeight);

  pdf.text("Total:", detalleX + paddingX, detalleY + 14);

  // Total general convertido a ARS si es necesario
  const totalImporte = data.items.reduce((sum, item) => {
    const cantidad = Number(item.cantidad || 0);
    const unitario = Number(item.precioUnitario || 0);
    const unitarioEnPesos = esARS ? unitario : unitario * tipoCambio;
    return sum + cantidad * unitarioEnPesos;
  }, 0);

  pdf.text(
    totalImporte.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    detalleX + col1W + col2W + col3W + paddingX,
    detalleY + 14
  );

  detalleY += totalRowHeight;

  return detalleY;
}

async function drawFirmaBlock(pdf, data, detalleX, detalleY, pageWidth) {
  const marginBottom = 40;
  const pageHeight = pdf.internal.pageSize.height;
  const maxY = pageHeight - marginBottom;

  const { width: imgFW, height: imgFH } = await loadImageSize(firma);
  const firmaW = 160;
  const firmaH = (imgFH / imgFW) * firmaW;

  const firmaBlockHeight = 15 + 15 + firmaH + 12 + 12 + 5;

  if (detalleY + firmaBlockHeight > maxY) {
    pdf.addPage();
    detalleY = 40;
  }

  detalleY += 40;

  pdf.setFont("Arial", "normal");
  pdf.setFontSize(10);

  // pdf.text(
  //   "En la fecha se recibe el informe y elementos correspondientes a la presente orden de servicio.",
  //   detalleX,
  //   detalleY
  // );
  // detalleY += 15;

  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];

  const [dia, mes, anio] = data.fecha.split("-");

  const fechaTexto = `San Juan, ${dia} de ${meses[parseInt(mes) - 1]
    } de ${anio}`;

  pdf.text(fechaTexto, detalleX, detalleY);
  detalleY += 15;

  // Agregar espacio para las firmas del solicitante y comitente
  detalleY += 80; // Espacio para las líneas de firma

  // Firmas lado a lado con separación de 1.8cm (51 puntos) de los bordes
  const separacionBorde = 51; // 1.8 cm en puntos
  const leftFirmaX = detalleX + separacionBorde;
  const rightFirmaX = pageWidth - 200 - separacionBorde; // Posición para la segunda firma

  pdf.setFont("Arial", "normal");
  pdf.setFontSize(10);

  // Firma Solicitante
  pdf.text("Firma Solicitante (Aclaración y DNI)", leftFirmaX, detalleY);

  // Firma Comitente
  pdf.text("Firma Comitente (Aclaración y DNI)", rightFirmaX, detalleY);

  // detalleY += 30;

  // const textRightX = pageWidth - 40;
  // pdf.setFont("Arial", "bold");
  // pdf.text("Dr. Marcelo Bellini", textRightX, detalleY, { align: "right" });
  // detalleY += 12;
  // pdf.text("Director IIM", textRightX, detalleY, { align: "right" });

  return detalleY;
}

async function crearPDF(data) {
  const pdf = new jsPDF({
    format: "a4",
    unit: "pt",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 40;

  let cursorY = await drawHeader(pdf, data, marginX, 40);
  cursorY = drawClientInfoTable(pdf, data, marginX, cursorY);

  // 🔁 Nuevo orden aquí:
  cursorY = drawImporte(
    pdf,
    marginX,
    cursorY,
    data.importeTotal,
    data.importeEnTexto,
    data.moneda,
    data.tipoDeCambio,
    pageWidth
  );

  cursorY = drawFormaPago(pdf, pageWidth, cursorY);
  cursorY = drawNotaEmails(pdf, cursorY);
  cursorY = drawCondicionesGenerales(pdf, pageWidth, cursorY);
  cursorY = drawPlazoYValidez(pdf, pageWidth, cursorY, data);

  // Ahora los ítems
  cursorY = drawDetalleTrabajo(pdf, data, marginX, cursorY, pageWidth);

  // Y luego la firma
  await drawFirmaBlock(pdf, data, marginX, cursorY, pageWidth);

  return pdf;
}

/**
 * Función pública para generar el PDF y según opción abrir o descargar
 * @param {*} data Datos de la orden
 * @param {Object} options Opciones: { openInNewTab: boolean }
 */
export async function generarOrdenServicioPDF(data, options = {}) {
  const { openInNewTab = false } = options;
  const pdf = await crearPDF(data);

  if (openInNewTab) {
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } else {
    pdf.save("orden_de_servicio.pdf");
  }
}
